import { useNavigate } from "react-router-dom";
import { ShoppingCart, Star } from "lucide-react";
import { DUMMY_PRODUCTS, useApp } from "@/context/AppContext";
import { AppButton } from "@/components/app/AppButton";

export default function ProductsPage() {
  const navigate = useNavigate();
  const { addToCart } = useApp();

  return (
    <main className="min-h-screen py-10 md:py-16">
      <div className="wrapper">
        {/* Page Header */}
        <div className="text-center mb-10">
          <span className="inline-block bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-3">
            Silver Collection
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            Our Products
          </h1>
          <p className="text-muted-foreground text-base max-w-xl mx-auto">
            Discover our handpicked collection of German silver rings —
            crafted to perfection for every occasion.
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {DUMMY_PRODUCTS.map((product) => (
            <div
              key={product.id}
              className="group bg-background border border-border rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col"
            >
              {/* Product Image */}
              <div
                className="relative overflow-hidden cursor-pointer"
                onClick={() => navigate(`/products/${product.id}`)}
              >
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className="bg-primary text-primary-foreground text-xs font-semibold px-2.5 py-1 rounded-full">
                    {product.category}
                  </span>
                </div>
                <div className="absolute top-3 right-3">
                  <span className="bg-background/90 text-foreground text-xs font-medium px-2.5 py-1 rounded-full">
                    {product.purity}
                  </span>
                </div>
              </div>

              {/* Product Info */}
              <div className="p-4 flex flex-col flex-1">
                <h3
                  className="font-semibold text-foreground text-base mb-1 cursor-pointer hover:text-primary transition-colors line-clamp-1"
                  onClick={() => navigate(`/products/${product.id}`)}
                >
                  {product.name}
                </h3>
                <p className="text-xs text-muted-foreground mb-2">
                  {product.material} · {product.weight}
                </p>

                {/* Rating */}
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                  <span className="text-xs text-muted-foreground ml-1">(48)</span>
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-2 mb-4 mt-auto">
                  <span className="text-lg font-bold text-foreground">
                    ₹{product.price.toLocaleString("en-IN")}
                  </span>
                  <span className="text-sm text-muted-foreground line-through">
                    ₹{product.originalPrice.toLocaleString("en-IN")}
                  </span>
                  <span className="text-xs text-green-600 font-semibold">
                    {Math.round(
                      ((product.originalPrice - product.price) /
                        product.originalPrice) *
                        100
                    )}
                    % off
                  </span>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <AppButton
                    size="sm"
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate(`/products/${product.id}`)}
                  >
                    View Details
                  </AppButton>
                  <AppButton
                    size="sm"
                    fullWidth
                    startIcon={<ShoppingCart className="h-3.5 w-3.5" />}
                    onClick={() => addToCart(product)}
                  >
                    Add to Cart
                  </AppButton>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
